// 職責：建立 router 實例並掛上認證／權限守衛（doc 06 §8.2、doc 09 §3.3）

/*
 * 為什麼是 history mode 而不是 hash（doc 06 §3）：
 *   舊版用 hash 是因為部署路徑會隨品牌變動。新版的 base 由 Vite 的 BASE_URL 決定，
 *   nginx 也已備妥 try_files fallback（doc 09 §8.3），沒有留下 `#/` 的理由 ——
 *   hash 讓 URL 無法被伺服器日誌、分析工具與 CDN 正確辨識。
 *
 * 守衛查的是目錄 key（`meta.permission`）對照 `session.permissionSet`，與門店 webapi
 * `[RequirePermission]` filter 讀同一批店碼（後端初期 log 模式，soak 完才 enforce，plan §7.2）。
 * 在後端 enforce 之前，守衛的價值仍然只是「不讓使用者走進一個必然失敗的頁面」。
 */

import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'

import { useSessionStore } from '@/stores/session'
import { runtimeConfig } from '@/api/runtime-config'

import { isRouteAllowed, REDIRECT_QUERY, resolveAfterLogin, routes } from './routes'

const router = createRouter({
  /*
   * BASE_URL 來自 vite.config.ts 的 base（`/taiyi/shopmgr/v2/`）。
   * 寫死字串會在部署路徑調整時產生「資源載得到、但每條路由都 404」的詭異狀態，
   * 因為 asset 前綴與 router base 各有一份來源。
   */
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...routes],

  scrollBehavior(_to, _from, savedPosition) {
    // 上一頁／下一頁時還原捲動位置，其餘一律回到頂端。
    // 不用 behavior: 'smooth'：main.css 已為 prefers-reduced-motion 全域關閉平滑捲動，
    // 這裡再指定一次只會讓兩處設定產生分歧。
    return savedPosition ?? { top: 0 }
  },
})

/*
 * 返回 URL 的兩個工具住在 routes.ts（與路由契約同一檔），此處只轉出。
 * 理由：選店頁也要用 `resolveAfterLogin`，而它若從本檔匯入，就會與「本檔動態載入 view」
 * 形成環狀相依。工具本身不需要 router 實例，放在契約檔才是它的正確位置。
 */
export { resolveAfterLogin, sanitizeRedirect } from './routes'

/* ────────────────────────────── 守衛 ────────────────────────────── */

router.beforeEach((to) => {
  // 守衛在 Pinia 安裝後才會執行（main.ts 先 use(createPinia)），此處取 store 是安全的
  const session = useSessionStore()

  /*
   * 已登入卻走回登入頁（例如按了上一頁）：不要讓使用者停在一個看起來像被登出的畫面。
   * 這裡消費 `?redirect=`，把他送回原本想去的地方。
   */
  if (to.name === 'login' && session.isAuthed) {
    return resolveAfterLogin(to)
  }

  if (to.meta.public === true) return true

  /*
   * 兩段式登入（doc 09 §3.3）：選店頁只需 ops session。
   * 若這裡用 isAuthed 判斷，剛完成集團登入、還沒有門店 token 的使用者會被彈回登入頁，
   * 形成一個永遠走不完的迴圈。
   */
  if (to.meta.opsOnly === true) {
    return session.isOpsAuthed ? true : loginWithRedirect(to)
  }

  if (!session.isAuthed) return loginWithRedirect(to)

  // ★ `meta.permission` 已經是目錄 key（深連結 `menu/:id` 與 `menu` 一樣是 `menu:view`），
  //   不再需要拿 route name 去查表 —— 那個做法正是 2026-09-03 浸會「模組進得去、
  //   一按編輯就 403」的根因。
  if (!isRouteAllowed(to.meta.permission, session.permissionSet, session.isSuperUser)) {
    // 導向 403 而不是靜默停留：使用者需要知道「這頁存在，但你沒權限」，才知道要找誰開通
    return { name: 'forbidden' }
  }

  return true
})

/** 頁面標題：品牌名走執行期設定，讓同一份 dist/ 能掛在不同品牌下（doc 06 §10.4） */
router.afterEach((to) => {
  const brand = runtimeConfig.title ?? 'ShopManager'
  document.title = to.meta.title === undefined ? brand : `${to.meta.title} · ${brand}`
})

/*
 * 動態 import 失敗最常見的原因不是「網路差」，而是**部署更新後舊 chunk 的檔名已不存在**。
 * 此時再多的重試都不會成功，唯一的出路是重新載入頁面取得新的 manifest。
 * 只在「使用者確實改變了位置」時重載，避免在啟動階段就陷入重載迴圈。
 *
 * 🚨 一定要用 `router.resolve(to).href`，不可以用 `to.fullPath`。
 *   vue-router 的 `fullPath` **不含 base** —— base 是在寫入 URL 那一刻才由
 *   `history.createHref()`（= `base + fullPath`）補回。在子路徑部署
 *   （浸會 `--base=/shop-manager/`）直接 `assign(to.fullPath)` 就會跳到
 *   `https://hkbu-food.scpos.online/employees`，而 nginx 的 SPA fallback
 *   只守住 `/shop-manager/*`（snippets/hp-hkbu/90-shopmgr.conf），
 *   所以出現的是 **nginx 硬 404 白頁**，不是重載成功。
 *   症狀是「有時按左邊 menu 就 404」—— 「有時」＝剛剛部署完、分頁仍持有
 *   舊 index.html，而 deploy 腳本 `rm -rf assets` 已經刪除舊 chunk。
 *   基於同一理由，下面「位置有沒有變」的比較亦要用含 base 的 href 去比較
 *   `location.pathname`，否則兩邊永遠不相等，防迴圈閘形同虛設。
 */
router.onError((error, to) => {
  const message = error instanceof Error ? error.message : String(error)
  if (
    /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message)
  ) {
    const href = router.resolve(to).href // 含 base，例如 /shop-manager/employees
    if (href !== window.location.pathname + window.location.search) {
      window.location.assign(href)
    }
  }
})

function loginWithRedirect(to: RouteLocationNormalized) {
  // 已經在登入頁時不再附帶 redirect，否則 query 會層層疊加
  const query = to.fullPath === '/login' ? {} : { [REDIRECT_QUERY]: to.fullPath }
  return { name: 'login', query }
}

export default router
